'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import useLocalization from '@/hooks/useLocalization';
import {
  CheckInSettings,
  ReservationWithRateDetailsDTO,
  Settings,
} from '@/models';
import { getShortDateFormat } from '@/lib/dateFormat';
import { formatPropertyDate } from '@/lib/dates';

import CheckInButton from '../check-in-button';

import styles from './index.module.scss';

interface CheckInDateProps {
  label: string;
  date: string;
  day: string;
  primaryColor: string;
}

const CheckInDate = ({
  label,
  date,
  day,
  primaryColor,
}: CheckInDateProps): React.ReactElement => {
  return (
    <div className={styles.centered}>
      <div className={clsx(styles.text, styles.gray)}>{label}</div>
      <div className={styles.date} style={{ color: primaryColor }}>
        {date}
      </div>
      <div className={styles.text}>{day}</div>
    </div>
  );
};

interface CheckInDatesProps {
  reservation: ReservationWithRateDetailsDTO;
  settings: Settings;
  primaryColor: string;
  checkInRoute: string;
  showCheckInButton: boolean;
  preview?: boolean;
  checkInSettings: CheckInSettings | null;
}

const CheckInDates = ({
  reservation,
  settings,
  primaryColor,
  checkInRoute,
  showCheckInButton,
  preview,
  checkInSettings,
}: CheckInDatesProps): React.ReactElement => {
  const intl = useIntl();
  const { dateFnsLocale } = useLocalization();
  const timezone = settings.whistle_guest_company_timezone;

  const shortFormat = getShortDateFormat(settings.date_format);
  const displayCheckInButton = !!(
    showCheckInButton &&
    !reservation.hasCheckedIn &&
    checkInSettings
  );

  return (
    <section className={styles.section}>
      <div className={styles.dates}>
        <CheckInDate
          label={intl.formatMessage({
            id: 'common/check-in',
            defaultMessage: 'Check-in',
          })}
          date={formatPropertyDate(
            reservation.startDate,
            timezone,
            shortFormat,
            dateFnsLocale,
          )}
          day={formatPropertyDate(
            reservation.startDate,
            timezone,
            'EEEE',
            dateFnsLocale,
          )}
          primaryColor={primaryColor}
        />
        <div className={styles.arrow} style={{ color: primaryColor }}>
          →
        </div>
        <CheckInDate
          label={intl.formatMessage({
            id: 'common/check-out',
            defaultMessage: 'Check-out',
          })}
          date={formatPropertyDate(
            reservation.endDate,
            timezone,
            shortFormat,
            dateFnsLocale,
          )}
          day={formatPropertyDate(
            reservation.endDate,
            timezone,
            'EEEE',
            dateFnsLocale,
          )}
          primaryColor={primaryColor}
        />
      </div>
      {displayCheckInButton && (
        <CheckInButton
          reservation={reservation}
          settings={settings}
          redirectPath={checkInRoute}
          preview={preview}
          checkInSettings={checkInSettings}
        />
      )}
    </section>
  );
};

export default CheckInDates;
